import { Component, Suspense, lazy, useMemo, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../../../shared/i18n/index.js';
import { BrandMark } from '../../../shared/brand/BrandMark.js';
import { LoginBackdrop } from './LoginBackdrop.js';

/**
 * Sahna **yuklanayotgan** paytdagi o'rindosh.
 *
 * Ilgari bu yerda `LoginBackdrop` ning yotiq relsli naqshi turardi va u
 * sahifa har yangilanganda bir zumga chaqnab ketardi — sahnaga hech
 * o'xshamagan boshqa rasm ko'rinib, keyin almashardi. Endi o'rindosh
 * shunchaki panelning o'z rangi: sahna kelganda `Canvas` foni ayni shu
 * rangda bo'ladi, shuning uchun almashish sezilmaydi.
 *
 * `@eombor/ui` dagi `Skeleton` bu yerda ATAYLAB ishlatilmadi: u ilova
 * sirtining (`--color-surface-2`) tokenlariga bog'langan, bu panel esa
 * ikkala temada ham quyuq va o'zining `--color-login-*` tokenlarida yashaydi.
 * Shakli ham bir xil — butun panelni to'ldiradi, shuning uchun sakrash yo'q.
 */
const SceneSkeleton = () => (
  <div aria-hidden className="bg-login-panel absolute inset-0 overflow-hidden">
    {/* Sekin "nafas": yuklanish uzoq cho'zilsa ham ekran o'lik ko'rinmasin.
        Yorug'lik markazda — sahnada ham koridor aynan shu yerdan ketadi. */}
    <div
      className="absolute inset-0 animate-pulse"
      style={{
        background:
          'radial-gradient(70% 55% at 50% 62%, color-mix(in oklab, var(--color-login-panel-2) 90%, transparent) 0%, transparent 70%)',
      }}
    />
  </div>
);

/** WebGL umuman yo'q kompyuterdagi zaxira: o'sha quyuq panel va SVG temir yo'l naqshi. */
const RailFallback = () => (
  <div className="bg-login-panel absolute inset-0">
    <LoginBackdrop patternClassName="text-login-ink opacity-[0.09]" />
  </div>
);

/**
 * `three` va uning yordamchilari ~600 KB. Ular kirish sahifasi bilan birga
 * yuklanmaydi: sahna faqat WebGL bor bo'lsa va faqat kerak bo'lganda keladi.
 */
const RailScene = lazy(async () => {
  const module = await import('./LoginRailScene.js');
  return { default: module.LoginRailScene };
});

/**
 * WebGL bormi. Ombordagi eski kompyuterlarda va virtual mashinalarda u
 * ko'pincha yo'q — kirish sahifasi bunday joyda **oq ekran** bermasligi kerak.
 */
const supportsWebGL = (): boolean => {
  // Avval konstruktor tekshiriladi: brauzersiz muhitda (test) `getContext`
  // umuman yo'q va uni chaqirish faqat konsolni ifloslantiradi.
  if (typeof WebGLRenderingContext === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    // Ba'zi brauzerlar siyosat sababli `getContext` da otadi — bu ham "yo'q".
    return false;
  }
};

/**
 * Sahna chizishda otsa ham sahifa qulamaydi: xato ushlanadi va o'sha zaxira
 * naqsh ko'rsatiladi. Kirish — yagona kirish nuqtasi, bezak uni to'sib
 * qo'ymasligi kerak.
 */
class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[login] 3D sahna chizilmadi, zaxira naqshga o\'tildi', error, info);
  }

  override render(): ReactNode {
    return this.state.failed ? <RailFallback /> : this.props.children;
  }
}

/**
 * Kirish sahifasining chap paneli — brend, sahna va shior.
 *
 * Panel ikkala temada ham quyuq (`--color-login-*` tokenlari): u ilova
 * sirtining davomi emas, fotosurat o'rnidagi vizual. `lg` dan kichik ekranda
 * u umuman chizilmaydi — omborda planshet va telefon ham ishlatiladi va
 * u yerda butun ekran formaga tegishli.
 */
export const LoginVisual = () => {
  const webgl = useMemo(supportsWebGL, []);
  const year = new Date().getFullYear();

  return (
    <aside className="bg-login-panel text-login-ink relative hidden overflow-hidden lg:block lg:w-[54%]">
      <div className="absolute inset-0">
        {webgl ? (
          <SceneBoundary>
            <Suspense fallback={<SceneSkeleton />}>
              <RailScene />
            </Suspense>
          </SceneBoundary>
        ) : (
          <RailFallback />
        )}
      </div>

      {/* Matn ostidagi tuslash — `text-shadow` o'rniga ATAYLAB fon qatlami:
          soya harflar chetini iflos qiladi, gradient esa matnni butun blok
          bo'ylab bir tekis ko'taradi. Ikki yo'nalish: chapdan (sahnaning
          tinch yarmi) va pastdan (sarlavha aynan shu yerda turadi).
          Ranglar tokenlardan, HEX yo'q. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: [
            'linear-gradient(to right, var(--color-login-panel) 0%, color-mix(in oklab, var(--color-login-panel) 88%, transparent) 24%, color-mix(in oklab, var(--color-login-panel) 52%, transparent) 42%, transparent 70%)',
            'linear-gradient(to top, var(--color-login-panel) 0%, color-mix(in oklab, var(--color-login-panel) 62%, transparent) 26%, transparent 58%)',
            // O'ng chet: javon qatori panel chegarasida keskin KESILMASIN,
            // balki panel rangiga singib ketsin. Kadr nisbati o'zgarganda
            // qaysi javon chetga tushishi har xil bo'ladi — bu qatlam esa
            // hamma nisbatda ishlaydi.
            'linear-gradient(to left, var(--color-login-panel) 0%, color-mix(in oklab, var(--color-login-panel) 45%, transparent) 6%, transparent 16%)',
          ].join(','),
        }}
      />

      <div className="relative flex h-full flex-col p-10 xl:p-12">
        <div className="flex items-center gap-3">
          {/* Quyuq panelda aksent yashili juda quyuq — belgi kataklar
              yorug'ligining rangida turadi va fondan ajralib chiqadi. */}
          <BrandMark className="text-login-glow size-9" />
          <div className="min-w-0">
            <p className="text-lg font-semibold tracking-tight">{t('app.name')}</p>
            <p className="text-login-ink-muted text-xs">{t('login.brandTagline')}</p>
          </div>
        </div>

        {/* `mt-auto` matnni panelning pastiga bosadi: aynan o'sha yerda
            pastdan ko'tarilgan gradient eng quyuq va sahna eng tinch —
            javon qatori kadrning o'ng yarmida qoladi. */}
        <div className="mt-auto mb-10">
          {/* `max-w-[16ch]` — sarlavha uzunligi qanday bo'lmasin ikki
              qatordan oshmaydi va tire bilan boshlanadigan qator chiqmaydi. */}
          <h2 className="max-w-[16ch] text-4xl font-semibold tracking-tight text-balance">
            {t('login.sceneTitle')}
          </h2>
          {/* Qisqa aksent bo'lagi — ilovadagi "rels" belgisining aks-sadosi. */}
          <span aria-hidden className="bg-login-glow my-5 block h-px w-12" />
          <p className="text-login-ink-muted max-w-[36ch] text-base text-pretty">
            {t('login.sceneLead')}
          </p>
        </div>

        <p className="text-login-ink-muted text-xs">
          {t('login.copyright', { year })}
        </p>
      </div>
    </aside>
  );
};
